import React from 'react';
import { PlusIcon, FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { HelpIcon } from '../HelpIcon';
import { TooltipProvider } from '../ui/tooltip';
import { getHelpContent } from '../../lib/helpContent';

interface CampaignEmptyStateProps {
  type: 'no-campaigns' | 'no-results' | 'no-contacts';
  onAction?: () => void;
  actionLabel?: string;
  onClearFilters?: () => void;
}

export const CampaignEmptyState: React.FC<CampaignEmptyStateProps> = ({
  type,
  onAction,
  actionLabel,
  onClearFilters,
}) => {
  // No campaigns exist
  if (type === 'no-campaigns') {
    const campaignWorkflowHelp = getHelpContent('campaignWorkflow');
    
    return (
      <TooltipProvider>
        <div className="text-center py-12">
          <svg
            className="mx-auto h-16 w-16 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
            />
          </svg>
          <div className="flex items-center justify-center gap-2 mt-4">
            <h3 className="text-lg font-medium text-gray-900">No campaigns yet</h3>
            {campaignWorkflowHelp && (
              <HelpIcon
                tooltip="Learn about creating campaigns"
                modalTitle={campaignWorkflowHelp.title}
                modalContent={campaignWorkflowHelp.content}
                size="sm"
                variant="subtle"
              />
            )}
          </div>
          <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
            Get started by creating your first marketing campaign. Reach out to your contacts with targeted messages.
          </p>
          {onAction && (
            <div className="mt-6">
              <button
                type="button"
                onClick={onAction}
                className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                {actionLabel || 'Create Campaign'}
              </button>
            </div>
          )}
        </div>
      </TooltipProvider>
    );
  }

  // No results from filters
  if (type === 'no-results') {
    return (
      <div className="text-center py-12">
        <FunnelIcon className="mx-auto h-16 w-16 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">No campaigns match your filters</h3>
        <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
          Try adjusting your search criteria or filters to find what you're looking for.
        </p>
        {onClearFilters && (
          <div className="mt-6">
            <button
              type="button"
              onClick={onClearFilters}
              className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    );
  }

  // No contacts in campaign
  if (type === 'no-contacts') {
    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-16 w-16 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-gray-900">No contacts in this campaign</h3>
        <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
          This campaign hasn't been started yet or no contacts matched the campaign filters.
        </p>
      </div>
    );
  }

  return null;
};

